import { getWarehouses, selectWarehouse } from 'containers/Warehouses/warehousesSlice'
import { selectUserId } from 'containers/Login/loginSlice'
import { useReduxDispatch, useReduxSelector } from 'store'
import { dateToYMDformat, toLongDate } from 'utils/date'
import { useHistory, useParams } from 'react-router-dom'
import ArrowBtn from 'components/buttons/ArrowBtn'
import useTranslation from 'hooks/useTranslation'
import BackBtn from 'components/buttons/BackBtn'
import ReactCalendar from 'react-calendar'
import React, { useState } from 'react'
import Option from 'components/Option'
import { POST } from 'rest/request'
import * as urls from 'rest/urls'

type DateType = Date | null
type RentalType = 'indefinite' | 'specified'
interface RentalExtensionProps { }

const RentalExtension: React.FC<RentalExtensionProps> = () => {
  const userId = useReduxSelector(selectUserId)
  const { id } = useParams<warehouse.PageParams>()
  const [isSending, setSending] = useState(false)
  const [date, setDate] = useState<Date | null>(null)
  const [type, setType] = useState<RentalType | undefined>(undefined)

  const warehouse = useReduxSelector(state => selectWarehouse(state, id))
  const minDate = new Date(
    (warehouse?.resignation?.type === 'termination' && warehouse?.resignation?.date)
      ? warehouse.resignation.date
      : warehouse?.fullDateTo
        ? warehouse.fullDateTo
        : warehouse?.inFuture
          ? warehouse.fullDateFrom
          : Date.now()
  )

  const { goBack } = useHistory()
  const dispatch = useReduxDispatch()
  const { t, lang } = useTranslation('warehouse')
  const send = async () => {
    if (isSending) return
    setSending(true)
    try {
      const body = type === 'indefinite'
        ? { indefiniteTime: true }
        : { newDate: dateToYMDformat(date!) }
      const response = await POST(urls.contractExtend(warehouse.contractId), { body })
      if (response?.result === 'ok') {
        dispatch(getWarehouses(userId))
        goBack()
      } else {
        throw new Error('wrong response')
      }
    } catch (error) {
      setSending(false)
      console.error(error)
    }
  }

  if (type === 'specified') {
    return (
      <div className='menu__section menu__extension'>
        <div className='title'>
          <div>{t('extendingYourRental')}</div>
          <div>{t('selectEndDate')}</div>
        </div>
        <div className='extension__main'>
          <div className='extension__calendar popup__calendar__container'>
            <ReactCalendar
              value={date}
              locale={lang}
              minDate={minDate}
              className='--light'
              defaultActiveStartDate={minDate}
              showNeighboringMonth={false}
              onChange={(value) => {
                setDate(value as DateType)
              }}
            />

            <div className='calendar__labels center'>
              <div>{t('end')}: <span>{date ? toLongDate(date, lang) : t('select')}</span></div>
            </div>

            <div className='calendar__info'>
              <b>{t('noticePeriod')}: </b>
              <div>{warehouse.terminationTerms?.infoForClient}</div>
            </div>
          </div>

          <div className='extension__buttons'>
            <ArrowBtn
              enlarged
              text={t('confirm')}
              isLoading={isSending}
              disabled={!date}
              action={() => {
                if (date) send()
              }}
            />

            <BackBtn
              text={t('back')}
              action={() => {
                setType(undefined)
              }}
            />
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className='menu__section menu__extension'>
      <div className='title'>
        <div>{t('extendingYourRental')}</div>
        <div>{t('selectRentalPeriod')}</div>
      </div>

      <div className='extension__main'>
        <div className='extension__steps --center'>
          <Option
            selected
            secondary={type !== 'indefinite'}
            title={t('forAnIndefinitePeriod')}
            desc={t('permanentContract')}
            buttonText={t(type === 'indefinite' ? 'selected' : 'select')}
            icon='infinite'
            action={() => {
              setType('indefinite')
            }}
          />

          <div className='or'>{t('or')}</div>

          <Option
            selected
            secondary
            title={t('forAFixedTerm')}
            desc={t('contractForAFixedPeriod')}
            buttonText={t('select')}
            icon='calendar-bold'
            action={() => {
              setType('specified')
            }}
          />

          <div className='calendar__info'>
            <b>{t('noticePeriod')}: </b>
            <div>{warehouse?.terminationTerms?.infoForClient}</div>
          </div>
        </div>

        <div className='extension__buttons'>
          <ArrowBtn
            enlarged
            text={t('confirm')}
            isLoading={isSending}
            disabled={!type}
            action={() => {
              if (type) send()
            }}
          />

          <BackBtn />
        </div>
      </div>
    </div>
  )
}

export default RentalExtension